// src/templates/empanadaclub/pages/franquiciado/FranquiciadoDashboard.jsx
// Dashboard personalizado de franquiciado para Empanada Club
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get } from '../../../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore,
    faUsers,
    faRocket,
    faArrowRight,
    faChartLine
} from '@fortawesome/free-solid-svg-icons';

const FranquiciadoDashboard = () => {
    const { franquiciadoId } = useParams();
    const [tiendas, setTiendas] = useState([]);
    const navigate = useNavigate();

    // Obtener las tiendas asociadas al franquiciado
    useEffect(() => {
        const fetchTiendas = async () => {
            try {
                const tiendaDetails = await get(`franquiciados/${franquiciadoId}/tiendas`);
                setTiendas(tiendaDetails);
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        };

        fetchTiendas();
    }, [franquiciadoId]);

    const handleViewClientes = (tiendaId) => {
        navigate(`/franquiciado/${franquiciadoId}/tiendas/${tiendaId}/clientes`);
    };

    const handleViewEstadisticas = (tiendaId) => {
        navigate(`/franquiciado/${franquiciadoId}/estadisticas/${tiendaId}`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                                <FontAwesomeIcon icon={faRocket} className="text-2xl" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold mb-2">Dashboard de Franquiciado</h1>
                                <p className="text-purple-100 text-lg">Gestiona tus tiendas, clientes y estadísticas de manera eficiente</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <FontAwesomeIcon icon={faStore} className="text-white text-xl" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Tiendas Asociadas</p>
                                <p className="text-2xl font-bold text-gray-900">{tiendas.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tiendas Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                            <FontAwesomeIcon icon={faStore} className="mr-3 text-purple-600" />
                            Mis Tiendas
                        </h2>
                        <div className="text-sm text-gray-500">
                            {tiendas.length} tienda{tiendas.length !== 1 ? 's' : ''} asociada{tiendas.length !== 1 ? 's' : ''}
                        </div>
                    </div>

                    {tiendas.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FontAwesomeIcon icon={faStore} className="text-gray-400 text-2xl" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes tiendas asignadas</h3>
                            <p className="text-gray-500">Contacta con el dueño del negocio para obtener acceso a tiendas.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {tiendas.map((tienda, index) => (
                                <div
                                    key={tienda._id}
                                    className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faStore} className="text-white text-lg" />
                                            </div>
                                            <div className="text-right">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Activa
                                                </span>
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{tienda.nombre}</h3>
                                        <p className="text-gray-600 mb-4 line-clamp-2">{tienda.descripcion || 'Sin descripción disponible'}</p>

                                        <div className="space-y-3">
                                            <button
                                                onClick={() => handleViewClientes(tienda._id)}
                                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center group"
                                            >
                                                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                                                Ver Clientes
                                                <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                            </button>

                                            <button
                                                onClick={() => handleViewEstadisticas(tienda._id)}
                                                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center group"
                                            >
                                                <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                                                Ver Estadísticas
                                                <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FranquiciadoDashboard;

